import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getManualTokens, hasManualTokens } from "@b3/chain-registry";

interface RelayCurrency {
  chainId: number;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  vmType: string;
  metadata: {
    logoURI?: string;
    verified: boolean;
    isNative: boolean;
  };
}

// Tokens that Relay doesn't return as `verified: true` but we want to surface
// in the selector. Keep this list small — the proper fix is getting the token
// verified on Relay.
const CURATED_TOKENS: RelayCurrency[] = [
  {
    chainId: 8453,
    address: "0xC17dda248E2D50fC006d8fEbb5A406dd31972712",
    symbol: "ANY",
    name: "AnySpend",
    decimals: 18,
    vmType: "evm",
    metadata: {
      logoURI: "https://cdn.b3.fun/anyspend-coin-3d.png",
      verified: true,
      isNative: false,
    },
  },
];

function matchesCuratedToken(
  token: RelayCurrency,
  chainId: number | undefined,
  searchTerm: string | undefined,
): boolean {
  if (chainId && token.chainId !== chainId) return false;
  if (!searchTerm) return true;
  const needle = searchTerm.toLowerCase();
  return (
    token.symbol.toLowerCase().includes(needle) ||
    token.name.toLowerCase().includes(needle) ||
    token.address.toLowerCase() === needle
  );
}

interface UseRelayTokenSearchOptions {
  chainId?: number;
  searchTerm?: string;
  enabled?: boolean;
  limit?: number;
}

interface UseRelayTokenSearchResult {
  tokens: RelayCurrency[];
  isLoading: boolean;
  error: Error | null;
}

export function useRelayTokenSearch({
  chainId,
  searchTerm,
  enabled = true,
  limit = 20,
}: UseRelayTokenSearchOptions): UseRelayTokenSearchResult {
  const query = useQuery({
    queryKey: ["relay-tokens", chainId, searchTerm, limit],
    queryFn: async () => {
      // Build request body
      const requestBody: any = {
        verified: true,
        limit: Math.min(limit, 100), // Relay API max is 100
      };

      // Only add chainIds if chainId is provided
      if (chainId) {
        requestBody.chainIds = [chainId];
      }

      // Add search term if provided
      if (searchTerm) {
        requestBody.term = searchTerm;
      } else {
        requestBody.defaultList = true; // Use default list if no search term
      }

      const response = await fetch("https://api.relay.link/currencies/v2", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(10_000),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tokens: ${response.statusText}`);
      }

      const data: RelayCurrency[] = await response.json();

      // Build the full curated list: static CURATED_TOKENS + manualTokens from
      // chain-registry for chains Relay doesn't index (e.g. Plume 98866).
      const registryExtras: RelayCurrency[] =
        chainId && hasManualTokens(chainId)
          ? getManualTokens(chainId).map(t => ({
              chainId,
              address: t.address.toLowerCase(),
              symbol: t.symbol,
              name: t.name,
              decimals: t.decimals,
              vmType: "evm",
              metadata: { verified: true, isNative: false, ...(t.logoUrl && { logoURI: t.logoUrl }) },
            }))
          : [];

      const seen = new Set(data.map(t => `${t.chainId}:${t.address.toLowerCase()}`));

      // CURATED_TOKENS are prepended (priority position).
      const curatedMatches = CURATED_TOKENS.filter(token => matchesCuratedToken(token, chainId, searchTerm));
      const curatedExtras = curatedMatches.filter(t => !seen.has(`${t.chainId}:${t.address.toLowerCase()}`));
      curatedExtras.forEach(t => seen.add(`${t.chainId}:${t.address.toLowerCase()}`));

      // Registry manual tokens are appended (supplemental, after Relay-indexed ones).
      const registryMatches = registryExtras.filter(token => matchesCuratedToken(token, chainId, searchTerm));
      const registryNew = registryMatches.filter(t => !seen.has(`${t.chainId}:${t.address.toLowerCase()}`));

      if (curatedExtras.length === 0 && registryNew.length === 0) return data;
      return [...curatedExtras, ...data, ...registryNew];
    },
    enabled: enabled, // Allow searching even without chainId
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    tokens: query.data || [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

// Hook with debounced search
export function useDebouncedRelayTokenSearch(chainId?: number, initialLimit = 20) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");

  // Debounce search term
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const updateSearchTerm = (term: string) => {
    setSearchTerm(term);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newTimeout = setTimeout(() => {
      setDebouncedTerm(term);
    }, 300);

    setTimeoutId(newTimeout);
  };

  const { tokens, isLoading, error } = useRelayTokenSearch({
    chainId,
    searchTerm: debouncedTerm,
    enabled: true, // Always enabled - can search with or without chainId
    limit: initialLimit,
  });

  return {
    tokens,
    isLoading,
    error,
    searchTerm,
    setSearchTerm: updateSearchTerm,
  };
}
